import React, { useState, useEffect } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';

interface OrderItem {
  name: string;
  price: number;
  quantity: number;
  productId: string;
}

interface PayPalCheckoutProps {
  orderItems: OrderItem[];
  totalAmount: number;
  onPaymentSuccess: (details: any) => void;
  onPaymentError: (error: any) => void;
  onPaymentCancel: () => void;
}

const PayPalCheckout: React.FC<PayPalCheckoutProps> = ({
  orderItems,
  totalAmount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel
}) => {
  const [{ isPending, isRejected, isInitial, isResolved }] = usePayPalScriptReducer();
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    console.log('PayPal Script Status:', { isPending, isRejected, isInitial, isResolved });
    console.log('Order Items:', orderItems);
    console.log('Total Amount:', totalAmount);
    
    setDebugInfo({
      isPending,
      isRejected,
      isInitial,
      isResolved,
      totalAmount,
      itemCount: orderItems.length
    });
    
    if (isRejected) {
      setScriptError('PayPal script failed to load. Please refresh the page and try again.');
    }
  }, [isPending, isRejected, isInitial, isResolved, orderItems, totalAmount]);

  const createOrder = (data: any, actions: any) => {
    console.log('Creating PayPal order for amount:', totalAmount);
    
    // Calculate item total
    const itemTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    const orderData = {
      purchase_units: [
        {
          amount: {
            value: itemTotal.toFixed(2), // Use itemTotal instead of totalAmount
            currency_code: 'USD',
            breakdown: {
              item_total: {
                currency_code: 'USD',
                value: itemTotal.toFixed(2)
              }
            }
          },
          description: 'ArtFul SVG Designs Purchase',
          items: orderItems.map(item => ({
            name: item.name,
            unit_amount: {
              currency_code: 'USD',
              value: item.price.toFixed(2)
            },
            quantity: item.quantity.toString()
          }))
        }
      ],
      application_context: {
        shipping_preference: 'NO_SHIPPING',
        user_action: 'PAY_NOW'
      }
    };
    
    console.log('PayPal order data:', orderData);
    
    return actions.order.create(orderData);
  };

  const onApprove = async (data: any, actions: any) => {
    try {
      console.log('PayPal order approved:', data);
      const details = await actions.order?.capture();
      console.log('PayPal payment captured:', details);
      
      // Create download records for the purchased items and get URLs
      const downloadData = await createDownloadsForItems(orderItems, details);
      
      // Pass download URLs to success handler
      onPaymentSuccess({
        ...details,
        downloads: downloadData.downloads || []
      });
      
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      onPaymentError(error);
    }
  };

  const createDownloadsForItems = async (items: OrderItem[], paymentDetails: any) => {
    try {
      // Call backend to create download records
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/create-for-purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          items: items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            price: item.price
          })),
          paymentId: paymentDetails.id,
          paymentStatus: 'completed'
        })
      });

      if (response.ok) {
        const downloadData = await response.json();
        console.log('Download records created:', downloadData);
        return downloadData; // Return the download data with URLs
      } else {
        console.error('Failed to create download records');
        return { downloads: [] };
      }
    } catch (error) {
      console.error('Error creating downloads:', error);
      return { downloads: [] };
    }
  };

  const onError = (err: any) => {
    console.error('PayPal error:', err);
    onPaymentError(err);
  };

  const onCancel = () => {
    console.log('PayPal payment cancelled');
    onPaymentCancel();
  };

  // For development testing - simulate successful payment with downloads
  const handleTestPayment = async () => {
    console.log('Simulating test payment with downloads');
    
    const mockDetails = {
      id: 'mock-payment-id',
      status: 'COMPLETED',
      create_time: new Date().toISOString(),
      intent: 'CAPTURE',
      payment_source: {
        paypal: {
          account_id: 'mock-account',
          email_address: 'test@example.com',
          name: {
            given_name: 'Test',
            surname: 'User'
          }
        }
      },
      purchase_units: [{
        reference_id: 'mock-order',
        amount: {
          currency_code: 'USD',
          value: totalAmount.toFixed(2)
        },
        payments: {
          captures: [{
            id: 'mock-capture',
            status: 'COMPLETED',
            amount: {
              currency_code: 'USD',
              value: totalAmount.toFixed(2)
            }
          }]
        }
      }]
    };

    // Create downloads for test payment
    await createDownloadsForItems(orderItems, mockDetails);
    
    onPaymentSuccess(mockDetails);
  };

  if (isPending || isInitial) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">
          Loading PayPal...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we connect to PayPal
        </Typography>
      </Box>
    );
  }

  if (isRejected || scriptError) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          PayPal script failed to load. Please use the test payment button below.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You can test the complete payment flow including download creation using the button below.
        </Typography>
        
        {/* Test Payment Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleTestPayment}
            sx={{ 
              mb: 2,
              backgroundColor: '#28a745',
              '&:hover': { backgroundColor: '#218838' },
              fontSize: '1.1rem',
              padding: '12px 24px'
            }}
            startIcon={
              <Box component="span" sx={{ 
                width: 24, 
                height: 24, 
                backgroundColor: 'white', 
                borderRadius: '50%',
                display: 'inline-block',
                mr: 1
              }} />
            }
          >
            🧪 Test Payment & Create Downloads
          </Button>
        </Box>
        
        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" component="div">
              Debug Info: {JSON.stringify(debugInfo, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        Complete Your Purchase
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: ${totalAmount.toFixed(2)}
        </Typography>
      </Box>

      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        onCancel={onCancel}
        style={{
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay'
        }}
      />
      
      {/* Test button for development */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleTestPayment}
          >
            Test Payment with Downloads
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PayPalCheckout; 